import { useEffect, useRef } from "react";
import { useFormContext, UseFormReturn, useWatch } from "react-hook-form";
import { BMSnackbarManager } from "../../../../components/BMSnackbar/BMSnackbar";
import { useMobileContentResource } from "../../../../core/resource/MobileResourceProvider";
import { useTravelerPickerContextV2 } from "../../../../providers/TravelerPickerContextV2";
import { formatMessage } from "../../../../utils";
import {
  MAX_FLIGHT_TRAVELER,
  MAX_HOTEL_CHILDREN,
  MAX_HOTEL_TRAVELER,
  MAX_INFANT,
} from "./constants";
import {
  Employee,
  FlightNonEmployee,
  FormValues,
  HotelNonEmployee,
} from "./types";

const DEFAULT_NON_EMPLOYEE = {
  adults: 0,
  children: 0,
  infants: 0,
  childrenAges: [],
} as FlightNonEmployee & HotelNonEmployee;

export type ErrorHandlerSelectedData = {
  employees?: Employee[];
  nonEmployees?: Partial<FlightNonEmployee & HotelNonEmployee>;
};
export default function useTravelerPickerErrorHandler(
  data: ErrorHandlerSelectedData,
  // This is the children form context
  selectMethods: UseFormReturn<any>
) {
  const {
    CorporateTravelerPicker: cr,
    CorporateFlightSearchForm: crFl,
    CorporateHotelSearchForm: crHl,
  } = useMobileContentResource();
  const { type } = useTravelerPickerContextV2();
  const lastNonEmployeeValues = useRef(DEFAULT_NON_EMPLOYEE);
  const { handleError, clearError } = useErrorHandlerLogic(selectMethods);

  // This is the parent form context
  const { control } =
    useFormContext<FormValues<FlightNonEmployee & HotelNonEmployee>>();
  const [employees, nonEmployee] =
    useWatch({ control, name: ["employees", "nonEmployee"] }) || {};

  useEffect(() => {
    switch (type) {
      case "flight": {
        const nonEmployeeAdults =
          data.nonEmployees?.adults ?? nonEmployee?.adults ?? 0;
        const numberOfAdults =
          (data.employees?.length ?? employees.length) + nonEmployeeAdults;
        const numberOfChildren =
          data.nonEmployees?.children ?? nonEmployee?.children ?? 0;
        const numberOfInfants =
          data.nonEmployees?.infants ?? nonEmployee?.infants ?? 0;
        const { root } = selectMethods.formState.errors;

        // Maximum number of travelers reached
        if (
          numberOfAdults + numberOfChildren === MAX_FLIGHT_TRAVELER &&
          root?.employees?.type === undefined
        ) {
          handleError(
            "root.employees",
            formatMessage(cr.maxTravelerLimitErrorMessage, {
              num: MAX_FLIGHT_TRAVELER,
            }),
            "304"
          );
        } else if (
          numberOfAdults + numberOfChildren < MAX_FLIGHT_TRAVELER &&
          root?.employees?.type === "304"
        ) {
          clearError("root.employees");
        }

        // No adults selected with children or infants
        if (
          numberOfAdults === 0 &&
          numberOfChildren + numberOfInfants > 0 &&
          root?.noAdultsSelected?.type === undefined
        ) {
          handleError("root.noAdultsSelected", crFl.nonEmployeeTooltipNoAdult);
        } else if (
          (numberOfAdults > 0 || numberOfChildren + numberOfInfants === 0) &&
          root?.noAdultsSelected?.type === "400"
        ) {
          clearError("root.noAdultsSelected");
        }

        // Maximum number of infants reached
        if (
          numberOfInfants === MAX_INFANT &&
          root?.infantsMaxReached?.type === undefined
        ) {
          handleError(
            "root.infantsMaxReached",
            formatMessage(crFl.errorTooltipMaxInfant, {
              maxInfant: MAX_INFANT,
            }),
            "304"
          );
        } else if (
          numberOfInfants < MAX_INFANT &&
          root?.infantsMaxReached?.type === "304"
        ) {
          clearError("root.infantsMaxReached");
        }

        // Each infant must be accompanied by at least one adult
        if (
          numberOfInfants > numberOfAdults &&
          root?.infantMoreThanAdult?.type === undefined
        ) {
          handleError(
            "root.infantMoreThanAdult",
            crFl.nonEmployeeTooltipLessAdult
          );
        } else if (
          numberOfInfants <= numberOfAdults &&
          root?.infantMoreThanAdult?.type === "400"
        ) {
          clearError("root.infantMoreThanAdult");
        }

        lastNonEmployeeValues.current.adults = nonEmployeeAdults;
        lastNonEmployeeValues.current.children = numberOfChildren;
        lastNonEmployeeValues.current.infants = numberOfInfants;
        break;
      }
      case "hotel": {
        const nonEmployeeAdults =
          data.nonEmployees?.adults ?? nonEmployee?.adults ?? 0;
        const numberOfAdults =
          (data.employees?.length ?? employees.length) + nonEmployeeAdults;
        const numberOfChildren =
          data.nonEmployees?.childrenAges?.length ??
          nonEmployee?.childrenAges.length ??
          0;
        const { root } = selectMethods.formState.errors;

        // Maximum number of travelers reached
        if (
          numberOfAdults === MAX_HOTEL_TRAVELER &&
          root?.employees?.type === undefined
        ) {
          handleError(
            "root.employees",
            formatMessage(cr.maxTravelerLimitErrorMessage, {
              num: MAX_HOTEL_TRAVELER,
            }),
            "304"
          );
        } else if (
          numberOfAdults < MAX_HOTEL_TRAVELER &&
          root?.employees?.type === "304"
        ) {
          clearError("root.employees");
        }

        // Maximum number of children reached
        if (
          numberOfChildren === MAX_HOTEL_CHILDREN &&
          root?.childrenAges?.type === undefined
        ) {
          handleError(
            "root.childrenAges",
            formatMessage(crHl.nonEmployeeTooltipMaxChildren, {
              maxChildren: MAX_HOTEL_CHILDREN,
            }),
            "304"
          );
        } else if (
          numberOfChildren < MAX_HOTEL_CHILDREN &&
          root?.childrenAges?.type === "304"
        ) {
          clearError("root.childrenAges");
        }

        // No adults selected with children
        if (
          numberOfAdults === 0 &&
          numberOfChildren > 0 &&
          root?.noAdultsSelected?.type === undefined
        ) {
          handleError("root.noAdultsSelected", crFl.nonEmployeeTooltipNoAdult);
        } else if (
          (numberOfAdults > 0 ||
            (numberOfAdults === 0 && numberOfChildren === 0)) &&
          root?.noAdultsSelected?.type === "400"
        ) {
          clearError("root.noAdultsSelected");
        }

        lastNonEmployeeValues.current.adults = nonEmployeeAdults;
        lastNonEmployeeValues.current.children = numberOfChildren;
        break;
      }
    }
  }, [data.employees, data.nonEmployees]);
}

function useErrorHandlerLogic(selectMethods: UseFormReturn<any>) {
  return {
    handleError(errorKey: string, message: string, type: string = "400") {
      selectMethods.setError(errorKey, {
        type,
      });
      BMSnackbarManager.show({
        message,
        type: type === "400" ? "RED" : "YELLOW",
        position: "TOP_CENTER",
      });
    },
    clearError(errorKey: string) {
      selectMethods.clearErrors(errorKey);
      BMSnackbarManager.close();
    },
  };
}
